import {
  autoUpdate,
  flip,
  FloatingPortal,
  shift,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
} from "@floating-ui/react";
import { BaseWidget } from "./BaseWidget";
import { useState } from "react";
import { SalesSummaryData } from "services/SalesService";
import { BsCalculator } from "react-icons/bs";
import { Card, Stack } from "@mui/material";
import VuiTypography from "components/VuiTypography";

interface Props {
  data?: SalesSummaryData;
  loading: boolean;
  currency: string;
}

export const ContributionMargin = ({ loading, currency, data }: Props) => {
  const [isOpen, setIsOpen] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: "bottom",
    middleware: [flip(), shift()],
    whileElementsMounted: autoUpdate,
  });

  const hover = useHover(context, { move: false });
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    focus,
    dismiss,
  ]);

  return (
    <>
      <div ref={refs.setReference} {...getReferenceProps()}>
        <BaseWidget
          title="Cont. Margin"
          value={data?.contributionMargin.value || 0}
          compareValue={data?.contributionMargin?.compareValue || 0}
          loading={loading}
          currency={currency}
          downIsGood={false}
          subtitle="Revenue - Spend - Cost - Shipping"
          showHoverIndicator
          icon={<BsCalculator color="white" />}
        />
      </div>
      <FloatingPortal>
        {isOpen && !loading && (
          <Card
            ref={refs.setFloating}
            {...getFloatingProps()}
            role="tooltip"
            style={{
              ...floatingStyles,
              width: 200,
              opacity: isOpen ? 1 : 0,
              transition: "opacity 0.2s",
              padding: "16px",
            }}
          >
            <Stack spacing={2}>
              <VuiTypography
                variant="body2"
                color="white"
                opacity={0.7}
                textTransform="capitalize"
                fontWeight="medium"
              >
                Cost breakdown
              </VuiTypography>

              <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between">
                  <VuiTypography
                    variant="caption"
                    color="white"
                    fontWeight="regular"
                  >
                    Revenue:
                  </VuiTypography>
                  <VuiTypography
                    variant="caption"
                    color="white"
                    fontWeight="regular"
                  >
                    {data?.revenue?.value?.toLocaleString("en-US", {
                      style: "currency",
                      currency: currency,
                    })}
                  </VuiTypography>
                </Stack>

                <Stack direction="row" justifyContent="space-between">
                  <VuiTypography
                    variant="caption"
                    color="white"
                    fontWeight="regular"
                  >
                    Shipping Paid:
                  </VuiTypography>
                  <VuiTypography
                    variant="caption"
                    color="white"
                    fontWeight="regular"
                  >
                    {data?.shippingPaid?.value?.toLocaleString("en-US", {
                      style: "currency",
                      currency: currency,
                    })}
                  </VuiTypography>
                </Stack>

                <Stack direction="row" justifyContent="space-between">
                  <VuiTypography
                    variant="caption"
                    color="white"
                    fontWeight="regular"
                  >
                    Tax Paid:
                  </VuiTypography>
                  <VuiTypography
                    variant="caption"
                    color="white"
                    fontWeight="regular"
                  >
                    {data?.taxPaid?.value.toLocaleString("en-US", {
                      style: "currency",
                      currency: currency,
                    })}
                  </VuiTypography>
                </Stack>

                <Stack direction="row" justifyContent="space-between">
                  <VuiTypography
                    variant="caption"
                    color="white"
                    fontWeight="regular"
                  >
                    COGS:
                  </VuiTypography>
                  <VuiTypography
                    variant="caption"
                    color="white"
                    fontWeight="regular"
                  >
                    {data?.cost?.value?.toLocaleString("en-US", {
                      style: "currency",
                      currency: currency,
                    })}
                  </VuiTypography>
                </Stack>

                <Stack direction="row" justifyContent="space-between">
                  <VuiTypography
                    variant="caption"
                    color="white"
                    fontWeight="regular"
                  >
                    Payment Processing Fee:
                  </VuiTypography>
                  <VuiTypography
                    variant="caption"
                    color="white"
                    fontWeight="regular"
                  >
                    {data?.paymentProcessingFee?.value?.toLocaleString("en-US", {
                      style: "currency",
                      currency: currency,
                    })}
                  </VuiTypography>
                </Stack>

                <Stack direction="row" justifyContent="space-between">
                  <VuiTypography
                    variant="caption"
                    color="white"
                    fontWeight="regular"
                  >
                    Ad spend:
                  </VuiTypography>
                  <VuiTypography
                    variant="caption"
                    color="white"
                    fontWeight="regular"
                  >
                    {data?.totalSpend?.value?.toLocaleString("en-US", {
                      style: "currency",
                      currency: currency,
                    })}
                  </VuiTypography>
                </Stack>

                <Stack direction="row" justifyContent="space-between">
                  <VuiTypography
                    variant="caption"
                    color="white"
                    fontWeight="regular"
                  >
                    Pick & Pack:
                  </VuiTypography>
                  <VuiTypography
                    variant="caption"
                    color="white"
                    fontWeight="regular"
                  >
                    {data?.pickAndPack?.value.toLocaleString("en-US", {
                      style: "currency",
                      currency: currency,
                    })}
                  </VuiTypography>
                </Stack>

              </Stack>

              {(data?.shippingPaid?.value === 0 ||
                data?.pickAndPack?.value === 0) && (
                <VuiTypography variant="caption" color="white" sx={{ mt: 2 }}>
                  Set "Pick & Pack" and "Payment Processing Fee" in shopify settings
                </VuiTypography>
              )}
            </Stack>
          </Card>
        )}
      </FloatingPortal>
    </>
  );
};
